import React, { PureComponent } from 'react'
import client from 'lib/graphql'
import { toaster } from 'evergreen-ui'
import { get } from 'lodash'

import EnableSmsStep from './components/Sms/EnableSmsStep'
import VerifySmsStep from './components/Sms/VerifySmsStep'
import RecoveryCodeStep from './components/RecoveryCodeStep'
import { MfaOptionTypes } from './MfaApp'

import { SETUP_SMS_MUTATION, SEND_SMS_QUERY, VERIFY_SMS_QUERY } from './graphql/sms'

enum SmsStep {
  enableSms = 'enableSms',
  verifySms = 'verifySms',
  recoveryCode = 'recoveryCode'
}

interface ConfigureSmsAppProps {
  configureTfaMethod: (option: MfaOptionTypes) => void
  refetchMfaStatus: any
}

interface ConfigureSmsAppState {
  step: SmsStep
  recoveryCode: string
  mobileNumber: string
  displayNumber: string
  countryCode: string
  tfaToken: string
}

class ConfigureSmsApp extends PureComponent<ConfigureSmsAppProps, ConfigureSmsAppState> {
  state = {
    step: SmsStep.enableSms,
    recoveryCode: '',
    mobileNumber: '',
    displayNumber: '',
    countryCode: '1',
    tfaToken: ''
  }

  // QUERIES
  onSetupSms = async () => {
    return client.mutate({
      mutation: SETUP_SMS_MUTATION,
      variables: {
        input: {
          phoneNumber: this.state.mobileNumber,
          countryCode: this.state.countryCode
        }
      }
    })
  }

  onSendSms = async () => {
    return client.query({
      query: SEND_SMS_QUERY,
      fetchPolicy: 'no-cache'
    })
  }

  onVerifySms = async () => {
    return client.query({
      query: VERIFY_SMS_QUERY,
      variables: {
        input: {
          token: this.state.tfaToken
        }
      }
    })
  }

  // FUNCTIONS
  onClickBackFromEnable = () => {
    this.setState({
      mobileNumber: '',
      countryCode: '1',
      displayNumber: ''
    })
    this.props.configureTfaMethod(MfaOptionTypes.none)
  }

  onClickBackFromVerify = () => {
    this.setState({
      step: SmsStep.enableSms,
      recoveryCode: '',
      tfaToken: '',
      mobileNumber: '',
      countryCode: '1',
      displayNumber: ''
    })
  }

  onClickEnable = async () => {
    // Setup sms
    let message = 'Something went wrong trying to enable your Text Message Multi-Factor Authentication'
    try {
      const setupRes = await this.onSetupSms()

      if (get(setupRes, 'data.setupSms.errors')) {
        return toaster.danger(message)
      }

      this.setState({
        recoveryCode: get(setupRes, 'data.setupSms.recoveryCode')
      })
    } catch (e) {
      return toaster.danger(message)
    }

    // Send sms
    message = 'Something went wrong trying to send your Text Message Multi-Factor Authentication code'
    try {
      const sendRes = await this.onSendSms()

      if (get(sendRes, 'data.sendSms.errors')) {
        return toaster.danger(message)
      }

      this.setState({ step: SmsStep.verifySms })
    } catch (e) {
      return toaster.danger(message)
    }
  }

  onClickVerify = async () => {
    const message = 'Something went wrong trying to enable your Text Message Multi-Factor Authentication'
    try {
      const verifyRes = await this.onVerifySms()
      if (get(verifyRes, 'data.verifySms.errors')) {
        return toaster.danger(message)
      }
      this.setState({ step: SmsStep.recoveryCode })
      toaster.success('You have successfully enabled Text Message Multi-Factor Authentication')
      this.props.refetchMfaStatus()
    } catch (e) {
      return toaster.danger(message)
    }
  }

  onClickResendLink = async () => {
    const messageFail = 'Something went wrong trying to send your Text Message Multi-Factor Authentication code'
    const messageSuccess = 'Text Message Multi-Factor Authentication code resent'

    try {
      const sendRes = await this.onSendSms()

      if (get(sendRes, 'data.sendSms.errors')) {
        return toaster.danger(messageFail)
      }
      return toaster.success(messageSuccess)
    } catch (e) {
      return toaster.danger(messageFail)
    }
  }

  onClickRecoveryDone = () => {
    this.setState({
      step: SmsStep.enableSms,
      recoveryCode: '',
      mobileNumber: '',
      countryCode: '1',
      displayNumber: '',
      tfaToken: ''
    })

    this.props.configureTfaMethod(MfaOptionTypes.none)
  }

  onMobileNumberInputChange = (value: string) => {
    this.setState({
      mobileNumber: value.replace(/\D+/g, ''),
      displayNumber: value
    })
  }

  onTfaTokenInputChange = (value: string) => {
    this.setState({
      tfaToken: value
    })
  }

  render() {
    const { step, recoveryCode, mobileNumber, displayNumber } = this.state

    if (step === SmsStep.enableSms) {
      return (
        <EnableSmsStep
          onClickBack={this.onClickBackFromEnable}
          onClickNext={this.onClickEnable}
          onMobileNumberInputChange={this.onMobileNumberInputChange}
          mobileNumber={mobileNumber}
        />
      )
    } else if (step === SmsStep.verifySms) {
      return (
        <VerifySmsStep
          onClickBack={this.onClickBackFromVerify}
          onClickEnable={this.onClickVerify}
          onClickResendLink={this.onClickResendLink}
          onTfaTokenInputChange={this.onTfaTokenInputChange}
          displayNumber={displayNumber}
        />
      )
    } else if (step === SmsStep.recoveryCode) {
      return <RecoveryCodeStep onClickNext={this.onClickRecoveryDone} recoveryCode={recoveryCode} />
    } else {
      return null
    }
  }
}

export default ConfigureSmsApp
